import { Injectable, inject } from '@angular/core';
import { ExportService } from '@eview/core/domain/export/export.service';
import { ExportDownloadService } from '@eview/core/domain/export/export-download.service';
import { UsersHelpers } from '@eview/core/users/users.helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  CreateJob,
  EExportActions,
  CreateJobSuccess,
  CreateJobFailure,
  CheckLastJobSuccess,
  LastJobSuccess,
  ExportPost,
  ExportPostSuccess,
  ExportPostFailure,
  ExportUsers,
  ExportUsersSuccess,
  ExportUsersFailure
} from '../actions/export.actions';
import { switchMap, catchError, map, tap } from 'rxjs/operators';
import { environment } from '@eview/core/environments/environment';
import { ExportJobStatus } from '@eview/core/domain/export/export';
import { AppState } from '../states/app.state';
import { Store } from '@ngrx/store';
import { selectExportJob } from '../selectors/export.selector';
import { of } from 'rxjs';

@Injectable()
export class ExportEffects {
  ExportJobStatus = ExportJobStatus;
  constructor(
    private exportService: ExportService,
    private exportDownload: ExportDownloadService,
    private store: Store<AppState>
  ) {}

  private readonly actions$ = inject(Actions);

  createJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateJob>(EExportActions.CreateJob),
      switchMap(action =>
        this.exportService.createJob(action.payload).pipe(
          map(payload =>
            payload ? new CreateJobSuccess(payload) : new CreateJobFailure()
          ),
          catchError(() => of(new CreateJobFailure()))
        )
      )
    )
  );

  checkLastJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateJobSuccess>(EExportActions.CreateJobSuccess),
      switchMap(() => {
        const timer = setInterval(() => {
          const obs = this.store
            .select(selectExportJob)
            .pipe(switchMap(jobs => this.exportService.readJob(jobs.last.id)))
            .subscribe(exportJob => {
              obs.unsubscribe();
              if (exportJob.status === ExportJobStatus.Failed) {
                clearInterval(timer);
              }
              if (exportJob.status != ExportJobStatus.Success) return;
              clearInterval(timer);
              this.store.dispatch(new LastJobSuccess(exportJob));
            });
        }, environment.export.timing * 1000) as any;
        return [new CheckLastJobSuccess(timer)];
      })
    )
  );

  exportPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ExportPost>(EExportActions.ExportPost),
      map(action => action.payload),
      switchMap(payload =>
        this.exportService.exportPosts(payload).pipe(
          map(result =>
            result.success === true
              ? new ExportPostSuccess(result)
              : new ExportPostFailure()
          ),
          catchError(() => of(new ExportPostFailure()))
        )
      )
    )
  );

  downloadExportedPosts$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<ExportPostSuccess>(EExportActions.ExportPostSuccess),
        tap(action => {
          const payload = action.payload;
          if (!payload?.success) {
            return;
          }
          const opts = this.exportDownload.consumePendingOptions();
          const rows = this.exportDownload.buildSheetRows(
            payload,
            opts?.columnKeys
          );
          if (!rows.length) {
            return;
          }
          UsersHelpers.ExportDataCSV(
            opts?.documentName || 'Reporte de informes',
            rows,
            opts?.formats || {
              excel: true,
              pdf: false
            }
          );
        })
      ),
    { dispatch: false }
  );

  exportUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ExportUsers>(EExportActions.ExportUsers),
      switchMap(() =>
        this.exportService.exportUsers().pipe(
          map(result =>
            result.success === true
              ? new ExportUsersSuccess(result)
              : new ExportUsersFailure()
          ),
          catchError(() => of(new ExportUsersFailure()))
        )
      )
    )
  );
}
